/**
 * Biometric Settings Component
 * Allows users to enable/disable fingerprint or Face ID login
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useBiometricAuth } from '../hooks/useBiometricAuth';
import { toast } from 'sonner';
import { 
  Fingerprint, 
  Scan, 
  Shield, 
  Loader2, 
  Check, 
  AlertTriangle,
  Smartphone
} from 'lucide-react';

const BiometricSettings = ({ userEmail }) => {
  const {
    isAvailable,
    biometryType,
    isEnabled,
    loading,
    enableBiometric,
    disableBiometric
  } = useBiometricAuth();

  const [showEnableDialog, setShowEnableDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [processing, setProcessing] = useState(false);

  // Don't render on web or if biometrics not available
  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
        </CardContent>
      </Card>
    );
  }

  if (!isAvailable) {
    return null; // Don't show if biometrics not available
  }

  const getBiometricIcon = () => {
    if (biometryType === 'Face ID') {
      return <Scan className="w-5 h-5" />;
    }
    return <Fingerprint className="w-5 h-5" />;
  };

  const handleToggle = async () => {
    if (isEnabled) {
      // Disable biometric
      setProcessing(true);
      try {
        await disableBiometric();
        toast.success(`${biometryType} login disabled`);
      } catch (error) {
        toast.error('Failed to disable biometric login');
      } finally {
        setProcessing(false);
      }
    } else {
      // Show dialog to enable
      setShowEnableDialog(true);
    }
  };

  const handleEnableBiometric = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setProcessing(true);
    try {
      await enableBiometric(userEmail, password);
      toast.success(`${biometryType} login enabled!`);
      setShowEnableDialog(false);
      setPassword('');
    } catch (error) {
      toast.error(error.message || 'Failed to enable biometric login');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <Card data-testid="biometric-settings-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            {getBiometricIcon()}
            <span className="text-emerald-500">{biometryType}</span> Login
          </CardTitle>
          <CardDescription>
            Unlock ParachPay quickly and securely with {biometryType?.toLowerCase() || 'biometrics'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl bg-muted">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isEnabled ? 'bg-emerald-500/10' : 'bg-muted'}`}>
                {getBiometricIcon()}
              </div>
              <div>
                <p className="font-medium">{biometryType} Authentication</p>
                <p className="text-sm text-muted-foreground">
                  {isEnabled 
                    ? 'Enabled - Use biometrics to unlock the app' 
                    : `Enable ${biometryType?.toLowerCase()} for faster login`
                  }
                </p>
              </div>
            </div>
            <Switch
              checked={isEnabled}
              onCheckedChange={handleToggle}
              disabled={processing}
              data-testid="biometric-toggle"
            />
          </div>

          {isEnabled && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
              <Check className="w-4 h-4" />
              <span className="text-sm">Your credentials are securely stored on this device</span>
            </div>
          )}

          {!isEnabled && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-muted text-sm text-muted-foreground">
              <Shield className="w-4 h-4 mt-0.5" />
              <div>
                <p>When enabled, you can log in using {biometryType?.toLowerCase()} instead of typing your password.</p>
                <p className="mt-1">Your credentials are stored securely in the device's {biometryType === 'Face ID' ? 'Keychain' : 'Keystore'}.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enable Biometric Dialog */}
      <Dialog open={showEnableDialog} onOpenChange={setShowEnableDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {getBiometricIcon()}
              Enable {biometryType} Login
            </DialogTitle>
            <DialogDescription>
              Enter your password to securely store your credentials for biometric authentication.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3 p-3 rounded-xl bg-orange-500/10">
              <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5" />
              <p className="text-sm text-orange-700 dark:text-orange-400">
                Your password will be stored securely on this device and protected by {biometryType?.toLowerCase()}.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="biometric-password">Current Password</Label>
              <Input
                id="biometric-password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 rounded-xl"
                data-testid="biometric-password-input"
              />
            </div>

            <Button
              onClick={handleEnableBiometric}
              disabled={processing || !password}
              className="w-full rounded-xl h-12"
              data-testid="enable-biometric-confirm-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Setting up...
                </>
              ) : (
                <>
                  {getBiometricIcon()}
                  <span className="ml-2">Enable {biometryType}</span>
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BiometricSettings;
